import { RouteComponentProps } from 'react-router-dom';
import ROUTES from '../../constants/routes';
import { world } from '../../languages';
import { WORLD_GROUPS } from '../../languages/constants';
import analytics from '../../services/analytics';
import qualityEvents from '../../services/quality-events';
import store from '../../store';
import isAuthorDomain from '../../utils/is-author-domain';
import makeUrl from '../../utils/url/make-url';
import { SEARCH_EVENT_TYPE, SEARCH_INPUT_TYPE, SOURCE_PAGE_TYPE } from './search.constants';

export type SearchConfig = {
    author: Author;
    isAuthorPage: boolean;
    postponedQE?: boolean;
    isInteractive?: boolean;
};

type Params = {
    [key: string]: string | boolean | number;
};

export type EventConfigType = {
    isCopyPasted?: boolean;
    isSuggestion?: boolean;
    query: string;
    type?: SEARCH_EVENT_TYPE;
};

type ApplySearchProps = {
    history: RouteComponentProps['history'];
    data: Params;
    config: SearchConfig;
    eventConfig: EventConfigType;
};

export const applySearch = (props: ApplySearchProps) => {
    const { history, data, eventConfig, config = {} as SearchConfig } = props;
    const { postponedQE = true, author = {} as Author, isAuthorPage, isInteractive } = config;
    let params = data;

    searchQualityEvents(eventConfig);

    if (postponedQE) {
        window.storage.setItem(
            'postponedQE',
            JSON.stringify({
                query: params.query,
                source: window.location.href.indexOf('searchResults') >= 0 ? 'search' : 'catalog',
                type: 'Request'
            })
        );
    }

    if (window.location.pathname.includes(ROUTES.FREE_MATERIALS_LANDING.path)) {
        params = Object.assign(params, { prc: '0' });
    }

    let url;
    const { isActiveNavigationAbTest } = store.getState().main.schoolClassification;

    if (isAuthorPage) {
        url = makeUrl(ROUTES.AUTHOR_MAIN.path, { id: author.id, slug: author.slug });
    } else if (isInteractive) {
        url = ROUTES.NEW_INTERACTIVE_MATERIALS.path;
    } else if (isActiveNavigationAbTest && !params.query) {
        url = ROUTES.SCHOOL_LANDING_PAGES.prefix;
    } else {
        url = ROUTES.SEARCH_RESULT.path;
    }

    const queryString = Object.keys(params)
        .filter((key: string) => params[key])
        .map((key) => `${key}=${encodeURIComponent(params[key])}`)
        .join('&');

    if (isAuthorDomain() || !history || isInteractive) {
        window.locationReload(`${url}?${queryString}`);
    } else {
        history.push(`${url}?${queryString}`);
    }
};

export const searchQualityEvents = ({ query, isSuggestion, isCopyPasted, ...props }: EventConfigType) => {
    let { type } = props;

    analytics.push({
        event: 'gtmEvent',
        eventAction: 'clickSearchButton',
        eventCategory: 'mkt-userInteraction',
        eventLabel: '',
        eventNonInteraction: false
    });

    if (WORLD_GROUPS.MAIN.includes(world)) {
        if (type === undefined) {
            if (query.length >= 3 && parseFloat(query)) {
                type = SEARCH_EVENT_TYPE.MATERIAL_BY_ID_SEARCH;
            }
        }

        if (type === undefined) {
            return;
        }

        qualityEvents.track({
            extra: JSON.stringify([
                ...(isSuggestion ? [SEARCH_INPUT_TYPE.SUGGESTION] : []),
                isCopyPasted ? SEARCH_INPUT_TYPE.COPIED : SEARCH_INPUT_TYPE.TYPED
            ]),
            source: getSearchSourcePage(),
            query,
            type
        });
    }
};

const getSearchSourcePage = (): SOURCE_PAGE_TYPE => {
    const { pathname } = window.location;
    const pageSources = {
        [SOURCE_PAGE_TYPE.SEARCH_RESULTS]: ROUTES.SEARCH_RESULT.pathnames,
        [SOURCE_PAGE_TYPE.MATERIAL_PAGE]: ROUTES.MATERIAL.prefixes,
        [SOURCE_PAGE_TYPE.AUTHOR_PAGE]: ROUTES.AUTHOR_MAIN.prefixes,
        [SOURCE_PAGE_TYPE.SUBJECT_PAGE]: ROUTES.LANDING.prefixes
    };

    if ([`/${world}`, `/${world}/`].includes(pathname)) {
        return SOURCE_PAGE_TYPE.HOME_PAGE;
    }

    const currentPageSources = Object.entries(pageSources).find(([_, val]) => pathname.includes(`${val[world]}`));

    return (currentPageSources ? currentPageSources[0] : SOURCE_PAGE_TYPE.OTHER) as SOURCE_PAGE_TYPE;
};
